import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Database, MessageSquare, FileText, Zap, Activity, Plus, ArrowRight,
  DollarSign, Hash, Gauge, Bot, Search, ChevronRight, Sparkles,
  Cpu, Brain, Wrench, Layers, AlertTriangle, CheckCircle2, Building2,
} from 'lucide-react';
import { agentApi, instancesApi, llmApi } from '../api';
import { Card, Badge, Button, Spinner } from '../components/UI';

const NODE_COLOR = {
  router:        { bd: '#6366f1', fg: '#a5b4fc' },
  retriever:     { bd: '#06b6d4', fg: '#67e8f9' },
  reranker:      { bd: '#22d3ee', fg: '#67e8f9' },
  critic:        { bd: '#f59e0b', fg: '#fcd34d' },
  synthesizer:   { bd: '#10b981', fg: '#6ee7b7' },
  citationVerifier: { bd: '#10b981', fg: '#6ee7b7' },
  cacheLookup:   { bd: '#facc15', fg: '#fde047' },
  cacheWrite:    { bd: '#facc15', fg: '#fde047' },
  memoryWriter:  { bd: '#a855f7', fg: '#d8b4fe' },
  summaryWriter: { bd: '#a855f7', fg: '#d8b4fe' },
};

// ════════════════════════════════════════════════════════════════════════════
//  HERO QUICK QUERY BAR
// ════════════════════════════════════════════════════════════════════════════
function QuickQueryBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const onSubmit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    const qs = new URLSearchParams({ q: q.trim() });
    navigate(`/agent?${qs.toString()}`);
  };
  return (
    <form onSubmit={onSubmit} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 18px',
      background:
        'radial-gradient(circle at 0% 0%, var(--accent-glow) 0%, transparent 50%),' +
        'radial-gradient(circle at 100% 100%, var(--accent2-glow) 0%, transparent 50%),' +
        'var(--bg-2)',
      border: '1px solid var(--border-bright)',
      borderRadius: 14,
      boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 32px var(--accent-glow)',
      marginBottom: 22,
    }}>
      <Sparkles size={20} style={{ color: 'var(--accent)' }} />
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Chiedi qualcosa alla tua knowledge base… (Invio per inviare all'agente)"
        style={{
          flex: 1, background: 'transparent', border: 'none', outline: 'none',
          color: 'var(--text-1)', fontSize: 15, fontFamily: 'inherit',
        }}
      />
      <button type="submit" disabled={!q.trim()} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 18px', borderRadius: 10,
        background: q.trim() ? 'linear-gradient(135deg, var(--accent), var(--accent-dim))' : 'var(--bg-3)',
        color: q.trim() ? '#fff' : 'var(--text-3)',
        border: 'none', cursor: q.trim() ? 'pointer' : 'not-allowed',
        fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13,
        boxShadow: q.trim() ? '0 4px 14px var(--accent-glow-strong)' : 'none',
        transition: 'all 0.2s',
      }}>
        Chiedi <ArrowRight size={14} />
      </button>
    </form>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  KPI CARD with mini-sparkline
// ════════════════════════════════════════════════════════════════════════════
function KpiCard({ label, value, icon: Icon, color, sparkline, format = 'num' }) {
  const series = sparkline || [];
  const max = Math.max(...series.map(s => s.value || 0), 0.000001);
  const min = Math.min(...series.map(s => s.value || 0), 0);
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.015) 0%, rgba(255,255,255,0) 60%), var(--bg-2)',
      border: '1px solid var(--border)', borderRadius: 14,
      padding: 16, boxShadow: 'var(--shadow-sm)',
      transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `var(--shadow), 0 0 24px ${color}40`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
    >
      {/* corner glow */}
      <div style={{
        position: 'absolute', top: -30, right: -30, width: 110, height: 110,
        borderRadius: '50%', background: color, opacity: 0.07, filter: 'blur(36px)',
        pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 8 }}>
            {label}
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color, lineHeight: 1, letterSpacing: '-0.5px' }}>
            {value}
          </div>
        </div>
        {Icon && (
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: `linear-gradient(135deg, ${color}25 0%, ${color}08 100%)`,
            border: `1px solid ${color}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 12px ${color}25`,
          }}>
            <Icon size={17} style={{ color }} />
          </div>
        )}
      </div>

      {/* Sparkline */}
      {series.length > 1 && (
        <div style={{ marginTop: 12, position: 'relative', height: 28 }}>
          <svg width="100%" height="28" preserveAspectRatio="none" viewBox={`0 0 100 28`}>
            <defs>
              <linearGradient id={`g-${label}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={color} stopOpacity="0.5" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            {(() => {
              const N = series.length;
              const pts = series.map((s, i) => {
                const x = (i / Math.max(1, N - 1)) * 100;
                const y = max === min ? 14 : 26 - ((s.value - min) / (max - min)) * 24;
                return `${x},${y}`;
              });
              return (
                <>
                  <polygon
                    points={`0,28 ${pts.join(' ')} 100,28`}
                    fill={`url(#g-${label})`}
                  />
                  <polyline
                    points={pts.join(' ')}
                    fill="none" stroke={color} strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </>
              );
            })()}
          </svg>
        </div>
      )}
    </div>
  );
}

function formatNum(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('it-IT');
}

// ════════════════════════════════════════════════════════════════════════════
//  AGENT PULSE — SVG of the graph topology, nodes light up on each new run
// ════════════════════════════════════════════════════════════════════════════
function AgentPulse({ pulse, totalLast24h, avgMs, cacheRate }) {
  const [active, setActive] = useState({}); // node -> expiry ts
  const tick = useRef(0);

  // When a new pulse arrives, light up the visited nodes for ~1.2s
  useEffect(() => {
    if (!pulse) return;
    const nodes = pulse.nodes || [];
    const exp = Date.now() + 1200;
    setActive(prev => {
      const next = { ...prev };
      nodes.forEach(n => { next[n] = exp; });
      return next;
    });
    // periodic cleanup
    const iv = setInterval(() => {
      const now = Date.now();
      setActive(prev => {
        const next = {};
        let changed = false;
        for (const [k, e] of Object.entries(prev)) {
          if (e > now) next[k] = e;
          else changed = true;
        }
        return changed ? next : prev;
      });
      tick.current++;
    }, 200);
    return () => clearInterval(iv);
  }, [pulse]);

  const NODE_W = 110, NODE_H = 36;
  const positions = {
    cacheLookup:   { x: 70,  y: 40 },
    router:        { x: 220, y: 40 },
    retriever:     { x: 360, y: 40 },
    critic:        { x: 500, y: 40 },
    synthesizer:   { x: 640, y: 40 },
    memoryWriter:  { x: 780, y: 40 },
  };
  const order = ['cacheLookup','router','retriever','critic','synthesizer','memoryWriter'];

  return (
    <Card style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 800, margin: 0 }}>
            🫀 Pulse del grafo agentico
          </h3>
          <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '3px 0 0' }}>
            Live: i nodi si illuminano ad ogni query completata
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
          <span style={{ color: 'var(--text-3)' }}>24h: <strong style={{ color: 'var(--accent)' }}>{totalLast24h ?? '—'}</strong></span>
          <span style={{ color: 'var(--text-3)' }}>avg: <strong style={{ color: 'var(--accent2)' }}>{avgMs ? Math.round(avgMs / 1000) + 's' : '—'}</strong></span>
          <span style={{ color: 'var(--text-3)' }}>cache: <strong style={{ color: 'var(--yellow)' }}>{((cacheRate || 0) * 100).toFixed(0)}%</strong></span>
        </div>
      </div>

      <div style={{ background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, overflowX: 'auto' }}>
        <svg viewBox="0 0 880 90" width="100%" height="120" style={{ minWidth: 720 }}>
          {/* edges */}
          {order.slice(0, -1).map((k, i) => {
            const a = positions[k], b = positions[order[i + 1]];
            return (
              <line key={k} x1={a.x + NODE_W / 2 - 6} y1={a.y}
                    x2={b.x - NODE_W / 2 + 6} y2={b.y}
                    stroke="var(--border-bright)" strokeWidth="1.5" />
            );
          })}

          {/* nodes */}
          {order.map(node => {
            const p = positions[node];
            const c = NODE_COLOR[node] || NODE_COLOR.router;
            const isActive = !!active[node];
            return (
              <g key={node}>
                {isActive && (
                  <circle cx={p.x} cy={p.y} r="28" fill={c.bd} opacity="0.25">
                    <animate attributeName="r" from="22" to="40" dur="1s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite" />
                  </circle>
                )}
                <rect
                  x={p.x - NODE_W / 2} y={p.y - NODE_H / 2}
                  width={NODE_W} height={NODE_H} rx={8}
                  fill={isActive ? c.bd : 'var(--bg-2)'}
                  stroke={isActive ? c.bd : 'var(--border)'}
                  strokeWidth={isActive ? 2 : 1}
                  style={{ transition: 'all 0.3s' }}
                />
                <text
                  x={p.x} y={p.y + 4} textAnchor="middle"
                  fill={isActive ? '#fff' : c.fg}
                  style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700 }}
                >
                  {node}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  LIVE ACTIVITY FEED
// ════════════════════════════════════════════════════════════════════════════
function LiveActivity({ activity, pulse }) {
  const navigate = useNavigate();
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (!pulse) return;
    setFlash(pulse.id);
    const tm = setTimeout(() => setFlash(null), 1500);
    return () => clearTimeout(tm);
  }, [pulse]);

  return (
    <Card style={{ padding: 18, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: 'var(--green)',
              boxShadow: '0 0 10px var(--green)',
              animation: 'pulseDot 1.5s ease-in-out infinite',
            }} />
            Live activity
          </h3>
          <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '3px 0 0' }}>Ultime query agentiche · click per il trace</p>
        </div>
        <Badge color="green">live</Badge>
      </div>

      {!activity || activity.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 12 }}>
          Nessuna query agentica ancora
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          {activity.map((r) => {
            const isFlash = flash === r.id;
            return (
              <div
                key={r.id}
                onClick={() => navigate(`/agent?tab=traces&trace=${r.id}`)}
                className={isFlash ? 'animate-fade-up' : ''}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr auto', gap: 10,
                  padding: '8px 10px',
                  background: isFlash ? 'var(--accent-glow)' : 'var(--bg-3)',
                  border: `1px solid ${isFlash ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: isFlash ? '0 0 20px var(--accent-glow-strong)' : 'none',
                }}
                onMouseEnter={e => !isFlash && (e.currentTarget.style.borderColor = 'var(--border-bright)')}
                onMouseLeave={e => !isFlash && (e.currentTarget.style.borderColor = 'var(--border)')}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-1)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
                    {r.query}
                  </div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 9, color: 'var(--text-3)', fontFamily: 'var(--font-mono)', flexWrap: 'wrap' }}>
                    {r.instanceName && <Badge color="cyan">{r.instanceName}</Badge>}
                    {r.tenantName && <Badge color="pink">🏢 {r.tenantName}</Badge>}
                    {r.iterations != null && <span>iter:{r.iterations}</span>}
                    {r.totalMs != null && <span>· {r.totalMs}ms</span>}
                    {r.costUsd > 0 && <span>· ${r.costUsd.toFixed(4)}</span>}
                    <span>· {new Date(r.createdAt).toLocaleTimeString('it-IT')}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {r.status === 'ok' ? <CheckCircle2 size={14} style={{ color: 'var(--green)' }} />
                    : r.status === 'fallback' ? <AlertTriangle size={14} style={{ color: 'var(--yellow)' }} />
                    : <AlertTriangle size={14} style={{ color: 'var(--red)' }} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  CHARTS ROW
// ════════════════════════════════════════════════════════════════════════════
function VolumeChart({ data }) {
  if (!data || data.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>Nessun dato</div>;
  const max = Math.max(...data.map(d => d.queries), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 130, paddingTop: 12 }}>
      {data.map((d, i) => {
        const h = (d.queries / max) * 100;
        return (
          <div key={i} title={`${d.day}: ${d.queries} query`} style={{
            flex: 1, minWidth: 6, height: h + '%',
            background: 'linear-gradient(180deg, var(--accent) 0%, var(--accent-dim) 100%)',
            borderRadius: '4px 4px 0 0',
            transition: 'all 0.4s',
            opacity: 0.85,
          }} />
        );
      })}
    </div>
  );
}

function SpendChart({ data }) {
  if (!data || data.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>Nessun dato</div>;
  const max = Math.max(...data.map(d => d.usd), 0.000001);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 130, paddingTop: 12 }}>
      {data.map((d, i) => {
        const h = Math.max(3, (d.usd / max) * 100);
        return (
          <div key={i} title={`${d.day}: $${d.usd.toFixed(4)} · ${d.tokens} tok`} style={{
            flex: 1, minWidth: 6, height: h + '%',
            background: 'linear-gradient(180deg, var(--green) 0%, #047857 100%)',
            borderRadius: '4px 4px 0 0',
            transition: 'all 0.4s', opacity: 0.85,
          }} />
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  LEADERBOARDS
// ════════════════════════════════════════════════════════════════════════════
function Leaderboard({ title, icon: Icon, rows, columns, color }) {
  return (
    <Card style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: `${color}20`, border: `1px solid ${color}40`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={14} style={{ color }} />
        </div>
        <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, margin: 0 }}>{title}</h4>
      </div>
      {(!rows || rows.length === 0) ? (
        <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'center', padding: 14 }}>—</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rows.slice(0, 5).map((r, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'center',
              padding: '7px 10px', borderRadius: 6,
              background: i === 0 ? `${color}15` : 'transparent',
            }}>
              <span style={{
                width: 18, height: 18, borderRadius: 4,
                background: i === 0 ? color : 'var(--bg-3)',
                color: i === 0 ? '#fff' : 'var(--text-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
              }}>{i + 1}</span>
              <span style={{ fontSize: 12, color: 'var(--text-1)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {columns.label(r)}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                {columns.value(r)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const { t } = useTranslation(['nav', 'instances']);
  const navigate = useNavigate();
  const [lastPulse, setLastPulse] = useState(null);

  const { data: dash, refetch: refetchDash } = useQuery({
    queryKey: ['agent-dashboard'], queryFn: () => agentApi.dashboard(30),
    refetchInterval: 15000,
  });
  const { data: activityResp, refetch: refetchActivity } = useQuery({
    queryKey: ['agent-activity'], queryFn: () => agentApi.activity(12),
    refetchInterval: 8000,
  });
  const { data: health } = useQuery({ queryKey: ['health'], queryFn: llmApi.health, refetchInterval: 30000 });

  // Subscribe to pulse SSE
  useEffect(() => {
    const es = agentApi.pulseStream({
      onPulse: (data) => {
        setLastPulse(data);
        refetchDash();
        refetchActivity();
      },
      onError: () => {},
    });
    return () => { try { es.close(); } catch {} };
  }, [refetchDash, refetchActivity]);

  const kpi = dash?.kpi || {};
  const sl = dash?.sparklines || {};
  const activity = activityResp?.activity || [];

  const cacheRatePct = (kpi.cacheRate || 0) * 100;
  const avgSec = kpi.avgMs ? (kpi.avgMs / 1000) : 0;

  return (
    <div className="animate-fade">
      {/* Compact header */}
      <div style={{
        padding: '24px 36px 18px',
        borderBottom: '1px solid var(--border)',
        background:
          'radial-gradient(circle at 0% 0%, var(--accent-glow) 0%, transparent 45%),' +
          'radial-gradient(circle at 100% 0%, var(--accent2-glow) 0%, transparent 45%),' +
          'linear-gradient(180deg, var(--bg-1) 0%, transparent 100%)',
      }}>
        <h1 className="gradient-text" style={{
          fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 30,
          lineHeight: 1.1, marginBottom: 6, letterSpacing: '-0.5px',
        }}>Dashboard</h1>
        <p style={{ color: 'var(--text-2)', fontSize: 13 }}>
          Panoramica live della tua piattaforma agentic RAG
        </p>
      </div>

      <div style={{ padding: '22px 36px', maxWidth: 1600, margin: '0 auto' }}>
        {/* 1. Quick query bar */}
        <QuickQueryBar />

        {/* 2. KPI grid (6 cards with sparklines) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 14, marginBottom: 22,
        }}>
          <KpiCard
            label="Istanze RAG"
            value={formatNum(kpi.instances)}
            icon={Database} color="var(--accent)"
          />
          <KpiCard
            label="Documenti"
            value={formatNum(kpi.documents)}
            icon={FileText} color="var(--accent2)"
          />
          <KpiCard
            label="Query (30g)"
            value={formatNum(kpi.queries30)}
            icon={MessageSquare} color="#a78bfa"
            sparkline={sl.queries}
          />
          <KpiCard
            label="Spesa USD (30g)"
            value={`$${(kpi.spend30 || 0).toFixed(3)}`}
            icon={DollarSign} color="var(--green)"
            sparkline={sl.cost}
          />
          <KpiCard
            label="Cache hit rate"
            value={`${cacheRatePct.toFixed(0)}%`}
            icon={Zap} color="var(--yellow)"
            sparkline={sl.cacheRate?.map(p => ({ day: p.day, value: p.value * 100 }))}
          />
          <KpiCard
            label="Latency media"
            value={avgSec > 0 ? `${avgSec.toFixed(1)}s` : '—'}
            icon={Gauge} color="var(--accent3)"
            sparkline={sl.latency?.map(p => ({ day: p.day, value: p.value / 1000 }))}
          />
        </div>

        {/* 3. Agent Pulse */}
        <div style={{ marginBottom: 22 }}>
          <AgentPulse
            pulse={lastPulse}
            totalLast24h={kpi.queries30}
            avgMs={kpi.avgMs}
            cacheRate={kpi.cacheRate}
          />
        </div>

        {/* 4. Activity + Charts row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 18, marginBottom: 22 }}>
          <LiveActivity activity={activity} pulse={lastPulse} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Card style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, margin: 0 }}>Volume query (30g)</h4>
                <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                  totale: {formatNum(kpi.queries30)}
                </span>
              </div>
              <VolumeChart data={dash?.charts?.queriesPerDay || []} />
            </Card>
            <Card style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, margin: 0 }}>Spesa giornaliera (30g)</h4>
                <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                  totale: ${(kpi.spend30 || 0).toFixed(4)}
                </span>
              </div>
              <SpendChart data={dash?.charts?.spendPerDay || []} />
            </Card>
          </div>
        </div>

        {/* 5. Leaderboards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 22 }}>
          <Leaderboard
            title="Top istanze"
            icon={Database} color="var(--accent)"
            rows={dash?.leaderboards?.topInstances}
            columns={{
              label: r => r.instanceName,
              value: r => `${r.queries} query`,
            }}
          />
          <Leaderboard
            title="Top tenant (spesa)"
            icon={Building2} color="var(--accent3)"
            rows={dash?.leaderboards?.topTenants}
            columns={{
              label: r => r.tenantName,
              value: r => `$${r.usd.toFixed(4)}`,
            }}
          />
          <Leaderboard
            title="Top modelli"
            icon={Cpu} color="var(--accent2)"
            rows={dash?.leaderboards?.topModels}
            columns={{
              label: r => r.model,
              value: r => `${r.calls}× · $${r.usd.toFixed(4)}`,
            }}
          />
        </div>

        {/* 6. Bottom row: instances list + system status */}
        <div className="grid-2col">
          <Card style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, margin: 0 }}>
                Le tue istanze RAG
              </h4>
              <Button variant="ghost" size="sm" icon={ArrowRight} onClick={() => navigate('/instances')}>Vai</Button>
            </div>
            <DashboardInstancesList navigate={navigate} />
          </Card>

          <Card style={{ padding: 16 }}>
            <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, margin: 0, marginBottom: 12 }}>
              ⚡ Stato sistema
            </h4>
            {health ? (
              <div>
                {Object.entries(health.services || {}).map(([key, svc]) => (
                  <ServiceStatus key={key} name={key} status={svc.status} url={svc.url} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><Spinner /></div>
            )}
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 9, color: 'var(--text-3)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700 }}>
                Accesso rapido
              </div>
              <Button variant="secondary" size="sm" icon={Bot}     onClick={() => navigate('/agent')}>Apri Agent</Button>
              <Button variant="secondary" size="sm" icon={Layers}  onClick={() => navigate('/instances')}>Gestisci istanze</Button>
              <Button variant="secondary" size="sm" icon={Building2} onClick={() => navigate('/tenants')}>Tenant B2B</Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Smaller sub-components used in the bottom row
function ServiceStatus({ name, status, url }) {
  const ok = status === 'online' || status === 'configured';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: ok ? 'var(--green)' : 'var(--red)',
          boxShadow: ok ? '0 0 8px var(--green)' : '0 0 8px var(--red)',
          animation: ok ? 'none' : 'pulse 1.5s infinite',
        }} />
        <span style={{ fontWeight: 600, color: 'var(--text-1)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.5px' }}>{name}</span>
      </div>
      <Badge color={ok ? 'green' : 'red'}>{status}</Badge>
    </div>
  );
}

function DashboardInstancesList({ navigate }) {
  const { data, isLoading } = useQuery({ queryKey: ['instances'], queryFn: instancesApi.list });
  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><Spinner /></div>;
  const instances = data?.instances || [];
  if (instances.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-3)' }}>
        <Database size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
        <div style={{ fontSize: 13, marginBottom: 12 }}>Nessuna istanza ancora</div>
        <Button size="sm" icon={Plus} onClick={() => navigate('/instances')}>Crea istanza</Button>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {instances.slice(0, 6).map(inst => (
        <div key={inst.id} onClick={() => navigate(`/instances/${inst.id}`)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
            transition: 'background 0.15s', border: '1px solid transparent',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>{inst.name}</div>
            <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
              {inst.stats?.documents || 0} docs · {inst.stats?.chunks || 0} chunks · {inst.stats?.queries || 0} query
            </div>
          </div>
          <Badge color="blue">{inst.qaProvider}</Badge>
        </div>
      ))}
    </div>
  );
}
